// libraries
import { useCallback, useEffect, useState } from 'react';
import { BackTop, Button, notification, Typography } from 'antd';
import { green } from '@ant-design/colors';
import styled from 'styled-components';

// components
import { ImageListDesktop } from './image-list-content.desktop';
import { ImageListMobile } from './image-list-content.mobile';

// utils
import * as UserService from '../../../../services/bb-api/user';
import { txt } from '../../../../locales/i18n';

// hooks
import { useDeviceSize } from '../../../../hooks/device-size';

export function UserImageList(): JSX.Element {
	const [dataSource, setDataSource] = useState<any[]>([]);
	const [totalMatches, setTotalMatches] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(true);
	const isMobileDevice: boolean = useDeviceSize();

	const getProfileImages = useCallback(async () => {
		setLoading(true);

		try {
			const profileImages: Record<string, any>[] = await UserService.getProfileImages();
			setDataSource(profileImages);

			const [entryProfileImage]: Record<string, any>[] = profileImages;
			setTotalMatches(entryProfileImage?.totalMatches ?? 0);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}, []);

	useEffect(() => {
		getProfileImages();
	}, [getProfileImages]);

	async function handleAccept(event: React.MouseEvent<HTMLElement, MouseEvent>, id: number) {
		event.stopPropagation();
		setLoading(true);

		try {
			await UserService.updateProfileImageApproval(id, true);
			notification.success({ message: txt('notification.update successful') });
			await getProfileImages();
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setLoading(false);
	}

	const handleAcceptAll = async () => {
		setLoading(true);

		try {
			const approveProfileImages = dataSource.map(async ({ imageId }) => {
				await UserService.updateProfileImageApproval(imageId, true);
			});

			await Promise.all(approveProfileImages);
			notification.success({ message: txt('notification.update successful') });
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		await getProfileImages();
		setLoading(false);
	};

	return (
		<StyledMain>
			<div className="topbar-container">
				<Typography.Text>
					{loading
						? 'loading...'
						: txt('global.table query notification', { amount: totalMatches })}
				</Typography.Text>

				{dataSource.length !== 0 && (
					<Button type="primary" className="approve-all-button" onClick={handleAcceptAll}>
						{txt('global.approve all')} ({dataSource.length})
					</Button>
				)}
			</div>

			{isMobileDevice === false && (
				<ImageListDesktop
					dataSource={dataSource}
					loading={loading}
					handleAccept={handleAccept}
				/>
			)}

			{isMobileDevice === true && (
				<ImageListMobile
					dataSource={dataSource}
					loading={loading}
					handleAccept={handleAccept}
				/>
			)}

			<BackTop target={(): any => document.querySelector('main')} />
		</StyledMain>
	);
}

const StyledMain = styled.main`
	display: flex;
	flex-direction: column;
	position: relative;

	.topbar-container {
		display: flex;
		align-items: center;
		gap: 2ch;
		margin: 1rem;

		.approve-all-button {
			margin-left: auto;
			background: ${green.primary};
			border-color: ${green.primary};
		}
	}
`;
